import { getIn } from 'formik'
import i18next from 'i18next'
import { LOCATIONS_RESET_CONFIG } from '../../../api/locations/formValuesConfig'
import { store } from '../../../store'
import { formatContactAsyncOption } from '../../../api/contacts/formatters'
import contacts from '../../../api/contacts/hooks'
import { formatProjectAsyncOption } from '../../../api/projects/formatters'
import projects from '../../../api/projects/hooks'
import { setFormFilter } from '../../../store/formFilters'
import { calculatePriceM2 } from '../../../utils/formUtils/fields'
import { renderAreaUnit, renderCurrency, renderCurrencyWithUnit } from '../../../utils/property'
import { REAL_ESTATE_TYPES } from '../../constants'
import requestFields from '../../constants/requestFields'
import {
    baseAsyncSelectInput,
    baseCheckboxInput,
    baseDateInput,
    baseMultiLanguageEditorInput,
    baseMultiLanguageTextInput,
    baseMultiSelectInput,
    baseNumeralInput,
    basePriceInput,
    baseSelectInput,
    baseTextAreaInput,
    baseTextInput,
    emptyLabel,
} from '../fieldTypes'
import {
    offerModalFields,
    propertyDetailSearchFields,
    realEstateDescriptionFields,
    realEstateGeneralFields,
    realEstateMaterials3DVideoFields,
} from './fields'
import { HRtransactionFieldTypes } from './transactionFieldsTypes/hr'
import { MEtransactionFieldTypes } from './transactionFieldsTypes/me'
import { SItransactionFieldTypes } from './transactionFieldsTypes/si'

const realEstateGeneralFieldTypes = (isEdit, appSettings, isOffice = false) => {
    const { t } = i18next

    return {
        [realEstateGeneralFields.EMPTY]: {
            ...emptyLabel,
            name: realEstateGeneralFields.EMPTY,
            label: '',
            colProps: {
                sm: 0,
                xxl: 8,
            },
        },
        [realEstateGeneralFields.EMPTY_COLUMN]: {
            ...emptyLabel,
            name: realEstateGeneralFields.EMPTY_COLUMN,
            label: '',
            colProps: {
                sm: 0,
                xxl: 12,
            },
        },
        [realEstateGeneralFields.ID]: {
            ...baseTextInput,
            name: realEstateGeneralFields.ID,
            label: t('app.common.id'),
            disabled: true,
            required: isEdit,
            colProps: {
                sm: 12,
            },
        },
        [realEstateGeneralFields.INTERNAL_ID]: {
            ...baseTextInput,
            name: realEstateGeneralFields.INTERNAL_ID,
            label: t('app.common.agencyId'),
            tooltip: t('form.property.general.agencyId.tooltip'),
            colProps: {
                sm: 12,
            },
        },
        [realEstateGeneralFields.PROPERTY_SERVICE_ID]: {
            ...baseSelectInput,
            colProps: {
                sm: 12,
            },
            name: realEstateGeneralFields.PROPERTY_SERVICE_ID,
            label: t('app.common.service'),
            disabled: isEdit,
            required: true,
            handleChange: (value) => {
                store.dispatch(setFormFilter({ context: 'property', field: 'propertyServiceId', value }))
            },
        },
        [realEstateGeneralFields.PROPERTY_TYPE_ID]: {
            ...baseSelectInput,
            colProps: {
                sm: 12,
            },
            name: realEstateGeneralFields.PROPERTY_TYPE_ID,
            label: t('app.common.property.type'),
            disabled: isEdit,
            required: true,
            handleChange: (value) => {
                store.dispatch(setFormFilter({ context: 'property', field: 'propertyTypeId', value }))
            },
        },
        [realEstateGeneralFields.PROPERTY_SUB_TYPE_ID]: {
            ...baseSelectInput,
            colProps: {
                sm: 12,
            },
            name: realEstateGeneralFields.PROPERTY_SUB_TYPE_ID,
            label: t('app.common.property.subtype'),
            required: true,
        },
        [realEstateGeneralFields.PROPERTY_LAND_USAGE_ID]: {
            ...baseSelectInput,
            colProps: {
                sm: 12,
            },
            name: realEstateGeneralFields.PROPERTY_LAND_USAGE_ID,
            label: t('app.common.property.landUsage'),
        },
        [realEstateGeneralFields.PROPERTY_PRIMARY_USAGE_ID]: {
            ...baseSelectInput,
            colProps: {
                sm: 12,
            },
            name: realEstateGeneralFields.PROPERTY_PRIMARY_USAGE_ID,
            label: t('app.common.property.primaryUsage'),
        },
        [realEstateGeneralFields.OWNER_ID]: {
            ...baseAsyncSelectInput,
            colProps: {
                sm: 12,
            },
            noLimit: true,
            searchCriteria: ['name'],
            name: realEstateGeneralFields.OWNER_ID,
            label: t('form.common.owner.nameOrCompany'),
            useEntityHook: contacts.GetContacts.useQuery,
            formatOption: formatContactAsyncOption,
        },
        [realEstateGeneralFields.PROJECT_ID]: {
            ...baseAsyncSelectInput,
            colProps: {
                sm: 12,
            },
            noLimit: true,
            searchCriteria: ['search'],
            name: realEstateGeneralFields.PROJECT_ID,
            label: t('app.common.project.label'),
            useEntityHook: projects.GetProjects.useQuery,
            formatOption: formatProjectAsyncOption,
        },
        [requestFields.PROPERTY_SUPPLY_SOURCE_ID]: {
            ...baseSelectInput,
            colProps: {
                sm: 12,
            },
            name: requestFields.PROPERTY_SUPPLY_SOURCE_ID,
            label: t('app.common.offerSource'),
        },
        [realEstateGeneralFields.CONTRACT_NO]: {
            ...baseTextInput,
            colProps: {
                sm: 12,
            },
            name: realEstateGeneralFields.CONTRACT_NO,
            label: t('app.common.contractNumber'),
        },
        [realEstateGeneralFields.CONTRACT_DATE]: {
            ...baseDateInput,
            colProps: {
                sm: 12,
            },
            name: realEstateGeneralFields.CONTRACT_DATE,
            label: t('app.common.contractDate'),
        },
        [realEstateGeneralFields.PROPERTY_RECEIPT_DATE]: {
            ...baseDateInput,
            colProps: {
                sm: 12,
            },
            name: realEstateGeneralFields.PROPERTY_RECEIPT_DATE,
            label: t('app.common.receiptDate'),
        },
        [realEstateGeneralFields.AVAILABLE_FROM_DATE]: {
            ...baseDateInput,
            colProps: {
                sm: 12,
            },
            name: realEstateGeneralFields.AVAILABLE_FROM_DATE,
            label: t('app.common.property.availableFrom'),
        },
        [realEstateGeneralFields.COUNTRY_ID]: {
            ...baseSelectInput,
            name: realEstateGeneralFields.COUNTRY_ID,
            label: t('app.common.country'),
            required: true,
            handleChange: (value, formik) => {
                store.dispatch(setFormFilter({ context: 'location', field: 'country', value }))

                LOCATIONS_RESET_CONFIG.country.forEach((field) => {
                    const fieldKey = `${field.toUpperCase()}_ID`
                    formik.setFieldValue(`general.${realEstateGeneralFields[fieldKey]}`, '')
                })
            },
        },
        [realEstateGeneralFields.COUNTY_ID]: {
            ...baseSelectInput,
            name: realEstateGeneralFields.COUNTY_ID,
            label: t('app.common.county'),
            required: true,
            handleChange: (value, formik) => {
                store.dispatch(setFormFilter({ context: 'location', field: 'county', value }))

                LOCATIONS_RESET_CONFIG.county.forEach((field) => {
                    const fieldKey = `${field.toUpperCase()}_ID`
                    formik.setFieldValue(`general.${realEstateGeneralFields[fieldKey]}`, '')
                })
            },
            handleInputDisable: (formik) => {
                const countryValue = getIn(formik?.values?.general, realEstateGeneralFields.COUNTRY_ID)
                return !countryValue || countryValue.length === 0
            },
        },
        [realEstateGeneralFields.CITY_ID]: {
            ...baseSelectInput,
            name: realEstateGeneralFields.CITY_ID,
            label: t('app.common.cityMunicipality'),
            required: true,
            handleChange: (value, formik) => {
                store.dispatch(setFormFilter({ context: 'location', field: 'city', value }))

                LOCATIONS_RESET_CONFIG.city.forEach((field) => {
                    const fieldKey = `${field.toUpperCase()}_ID`
                    formik.setFieldValue(`general.${realEstateGeneralFields[fieldKey]}`, '')
                })
            },
            handleInputDisable: (formik) => {
                const countyValue = getIn(formik?.values?.general, realEstateGeneralFields.COUNTY_ID)
                return !countyValue || countyValue.length === 0
            },
        },
        [realEstateGeneralFields.WIDER_AREA_ID]: {
            ...baseSelectInput,
            name: realEstateGeneralFields.WIDER_AREA_ID,
            label: t('app.common.widerArea'),
            required: true,
            handleChange: (value, formik) => {
                store.dispatch(setFormFilter({ context: 'location', field: 'widerArea', value }))

                LOCATIONS_RESET_CONFIG.wider_area.forEach((field) => {
                    const fieldKey = `${field.toUpperCase()}_ID`
                    formik.setFieldValue(`general.${realEstateGeneralFields[fieldKey]}`, '')
                })
            },
            handleInputDisable: (formik) => {
                const cityValue = getIn(formik?.values?.general, realEstateGeneralFields.CITY_ID)
                return !cityValue || cityValue.length === 0
            },
        },
        [realEstateGeneralFields.QUARTER_ID]: {
            ...baseSelectInput,
            name: realEstateGeneralFields.QUARTER_ID,
            label: t('app.common.quarter'),
            handleInputDisable: (formik) => {
                const widerAreaValue = getIn(formik?.values?.general, realEstateGeneralFields.WIDER_AREA_ID)
                return !widerAreaValue || widerAreaValue.length === 0
            },
        },
        [realEstateGeneralFields.ISLAND_ID]: {
            ...baseSelectInput,
            name: realEstateGeneralFields.ISLAND_ID,
            label: t('app.common.island'),
            handleInputDisable: (formik) => {
                const countryValue = getIn(formik?.values?.general, realEstateGeneralFields.COUNTRY_ID)
                return !countryValue || countryValue.length === 0
            },
        },
        [realEstateGeneralFields.STREET]: {
            ...baseTextInput,
            name: realEstateGeneralFields.STREET,
            label: t('app.common.street'),
        },
        [realEstateGeneralFields.STREET_NUMBER]: {
            ...baseTextInput,
            name: realEstateGeneralFields.STREET_NUMBER,
            label: t('app.common.streetNumber'),
        },
        [realEstateGeneralFields.STREET_SYNC]: {
            ...baseCheckboxInput,
            colProps: {
                sm: 12,
                xxl: 2,
                className: 'd-flex align-items-center',
            },
            name: realEstateGeneralFields.STREET_SYNC,
            label: t('form.common.streetSync.label'),
            tooltip: t('form.common.streetSync.tooltip'),
        },
        [realEstateGeneralFields.PROPERTY_LABEL]: {
            ...baseTextInput,
            name: realEstateGeneralFields.PROPERTY_LABEL,
            label: t('app.common.property.designation'),
            tooltip: t('form.property.general.designation.tooltip'),
        },
        [realEstateGeneralFields.PROPERTY_POSITION_ID]: {
            ...baseSelectInput,
            name: realEstateGeneralFields.PROPERTY_POSITION_ID,
            label: t('app.common.property.position'),
        },
        [realEstateGeneralFields.PRICE]: {
            ...basePriceInput,
            name: realEstateGeneralFields.PRICE,
            label: t('app.common.price', { currency: renderCurrency(appSettings?.currency) }),
            tooltip: t('app.common.price.tooltip'),
            required: true,
            handleChange: (value, formik) => {
                const area = getIn(formik.values, 'general.area')
                const calculatedPriceM2 = calculatePriceM2(formik, value, area)
                formik.setFieldValue('general.price_m2', calculatedPriceM2)
            },
        },
        [realEstateGeneralFields.PRICE_INITIAL]: {
            ...basePriceInput,
            name: realEstateGeneralFields.PRICE_INITIAL,
            label: t('app.common.property.initialPrice', { currency: renderCurrency(appSettings?.currency) }),
            required: false,
        },
        [realEstateGeneralFields.PRICE_CLIENT]: {
            ...basePriceInput,
            name: realEstateGeneralFields.PRICE_CLIENT,
            label: t('app.common.property.priceClient', { currency: renderCurrency(appSettings?.currency) }),
        },
        [realEstateGeneralFields.PRICE_M2]: {
            ...basePriceInput,
            name: realEstateGeneralFields.PRICE_M2,
            label: t('app.common.pricePerUnit', {
                currencyWithUnit: renderCurrencyWithUnit(appSettings?.currency, appSettings?.areaUnit),
            }),
        },
        [realEstateGeneralFields.PRICE_OLD]: {
            ...basePriceInput,
            name: realEstateGeneralFields.PRICE_OLD,
            label: t('app.common.property.oldPrice', { currency: renderCurrency(appSettings?.currency) }),
            tooltip: t('app.common.property.oldPrice.tooltip'),
        },
        [realEstateGeneralFields.VAT_INCLUDED]: {
            ...baseCheckboxInput,
            colProps: {
                sm: 12,
                xxl: 4,
                className: 'd-flex align-items-center',
            },
            name: realEstateGeneralFields.VAT_INCLUDED,
            label: t('app.common.property.vatIncluded'),
        },
        [realEstateGeneralFields.VAT_NOT_INCLUDED]: {
            ...baseCheckboxInput,
            colProps: {
                sm: 12,
                xxl: 4,
                className: 'd-flex align-items-center',
            },
            name: realEstateGeneralFields.VAT_NOT_INCLUDED,
            label: t('app.common.property.vatNotIncluded'),
        },
        [realEstateGeneralFields.UTILITIES_INCLUDED]: {
            ...baseCheckboxInput,
            colProps: {
                sm: 12,
                xxl: 4,
                className: 'd-flex align-items-center',
            },
            name: realEstateGeneralFields.UTILITIES_INCLUDED,
            label: t('form.property.general.utilitiesIncluded'),
        },
        [realEstateGeneralFields.EXCHANGE_POSSIBLE]: {
            ...baseCheckboxInput,
            colProps: {
                sm: 12,
                xxl: 4,
                className: 'd-flex align-items-center',
            },
            name: realEstateGeneralFields.EXCHANGE_POSSIBLE,
            label: t('form.property.general.exchangePossible'),
        },
        [realEstateGeneralFields.PRICE_ON_REQUEST]: {
            ...baseCheckboxInput,
            colProps: {
                sm: 12,
                xxl: 4,
                className: 'd-flex align-items-center',
            },
            name: realEstateGeneralFields.PRICE_ON_REQUEST,
            label: t('app.common.property.priceOnRequest'),
            tooltip: t('form.property.general.priceOnRequest.tooltip'),
        },
        [realEstateGeneralFields.MAINTENANCE_COSTS]: {
            ...basePriceInput,
            name: realEstateGeneralFields.MAINTENANCE_COSTS,
            colProps: {
                sm: 7,
                className: 'pe-0',
            },
        },
        [realEstateGeneralFields.MAINTENANCE_COSTS_TYPE]: {
            ...baseSelectInput,
            name: realEstateGeneralFields.MAINTENANCE_COSTS_TYPE,
            isClearable: false,
            colProps: {
                sm: 5,
            },
        },
        [realEstateGeneralFields.COMMON_MAINTENANCE_COSTS]: {
            ...basePriceInput,
            name: realEstateGeneralFields.COMMON_MAINTENANCE_COSTS,
            colProps: {
                sm: 7,
                className: 'pe-0',
            },
        },
        [realEstateGeneralFields.COMMON_MAINTENANCE_COSTS_TYPE]: {
            ...baseSelectInput,
            name: realEstateGeneralFields.COMMON_MAINTENANCE_COSTS_TYPE,
            isClearable: false,
            colProps: {
                sm: 5,
            },
        },
        [realEstateGeneralFields.COMMON_UTILITY_COSTS]: {
            ...basePriceInput,
            name: realEstateGeneralFields.COMMON_UTILITY_COSTS,
            colProps: {
                sm: 7,
                className: 'pe-0',
            },
        },
        [realEstateGeneralFields.COMMON_UTILITY_COSTS_TYPE]: {
            ...baseSelectInput,
            name: realEstateGeneralFields.COMMON_UTILITY_COSTS_TYPE,
            isClearable: false,
            colProps: {
                sm: 5,
            },
        },
        [realEstateGeneralFields.PARKING_SPACE_PRICE]: {
            ...basePriceInput,
            name: realEstateGeneralFields.PARKING_SPACE_PRICE,
            label: t('form.property.general.parkingSpacePrice', { currency: renderCurrency(appSettings?.currency) }),
        },
        [realEstateGeneralFields.MORTGAGE_INFO]: {
            ...basePriceInput,
            name: realEstateGeneralFields.MORTGAGE_INFO,
            label: t('app.common.property.mortgage'),
        },
        [realEstateGeneralFields.PROPERTY_HOUSE_TYPE_ID]: {
            ...baseSelectInput,
            name: realEstateGeneralFields.PROPERTY_HOUSE_TYPE_ID,
            label: t('app.common.property.houseType'),
        },
        [realEstateGeneralFields.AREA]: {
            ...baseNumeralInput,
            name: realEstateGeneralFields.AREA,
            label: t('app.common.area', { areaUnit: renderAreaUnit(appSettings?.areaUnit) }),
            required: true,
            handleChange: (value, formik) => {
                const price = getIn(formik.values, 'general.price')
                const calculatedPriceM2 = calculatePriceM2(formik, price, value)
                formik.setFieldValue('general.price_m2', calculatedPriceM2)
            },
        },
        [realEstateGeneralFields.AREA_NETT]: {
            ...baseNumeralInput,
            name: realEstateGeneralFields.AREA_NETT,
            label: t('form.property.general.areaNett', { areaUnit: renderAreaUnit(appSettings?.areaUnit) }),
            tooltip: t('form.property.general.areaNett.tooltip'),
        },
        [realEstateGeneralFields.AREA_GARDEN]: {
            ...baseNumeralInput,
            name: realEstateGeneralFields.AREA_GARDEN,
            label: t('app.common.property.yard', { areaUnit: renderAreaUnit(appSettings?.areaUnit) }),
        },
        [realEstateGeneralFields.AREA_STORAGE]: {
            ...baseNumeralInput,
            name: realEstateGeneralFields.AREA_STORAGE,
            label: t('form.property.general.areaStorage', { areaUnit: renderAreaUnit(appSettings?.areaUnit) }),
        },
        [realEstateGeneralFields.PLOT_AREA]: {
            ...baseNumeralInput,
            name: realEstateGeneralFields.PLOT_AREA,
            label: t('app.common.property.areaPlot', { areaUnit: renderAreaUnit(appSettings?.areaUnit) }),
        },
        [realEstateGeneralFields.PLOT_WIDTH]: {
            ...baseNumeralInput,
            name: realEstateGeneralFields.PLOT_WIDTH,
            label: t('app.common.property.plotWidth'),
        },
        [realEstateGeneralFields.PLOT_LENGTH]: {
            ...baseNumeralInput,
            name: realEstateGeneralFields.PLOT_LENGTH,
            label: t('app.common.property.plotLength'),
        },
        [realEstateGeneralFields.CONSTRUCTION_COEFFICIENT]: {
            ...baseNumeralInput,
            name: realEstateGeneralFields.CONSTRUCTION_COEFFICIENT,
            label: t('app.common.property.constructionCoefficient'),
        },
        [realEstateGeneralFields.CONSTRUCTION_FLOORS_NO]: {
            ...baseNumeralInput,
            name: realEstateGeneralFields.CONSTRUCTION_FLOORS_NO,
            label: t('app.common.property.constructionFloorNo'),
        },
        [realEstateGeneralFields.MAX_CONSTRUCTION_AREA]: {
            ...baseNumeralInput,
            name: realEstateGeneralFields.MAX_CONSTRUCTION_AREA,
            label: t('form.property.general.maxConstructionArea'),
        },
        [realEstateGeneralFields.PROPERTY_ROOMS_NO_ID]: {
            ...baseSelectInput,
            name: realEstateGeneralFields.PROPERTY_ROOMS_NO_ID,
            label: t('app.common.rooms'),
            required: true,
        },
        [realEstateGeneralFields.BEDROOMS_NO]: {
            ...baseTextInput,
            name: realEstateGeneralFields.BEDROOMS_NO,
            label: t('app.common.bedrooms'),
            tooltip: t('app.common.bedrooms.tooltip'),
            required: false,
        },
        [realEstateGeneralFields.HOUSING_UNITS_NUMBER]: {
            ...baseTextInput,
            name: realEstateGeneralFields.HOUSING_UNITS_NUMBER,
            tooltip: t('app.common.property.housingUnitsNumber.tooltip'),
            label: t('app.common.property.housingUnitsNumber'),
        },
        [realEstateGeneralFields.ROOMS_NO]: {
            ...baseTextInput,
            name: realEstateGeneralFields.ROOMS_NO,
            required: true,
            tooltip: t(`form.property.general.${isOffice ? 'roomsNoOffice' : 'roomsNo'}.tooltip`),
            label: t('form.property.general.roomsNo'),
        },
        [realEstateGeneralFields.BED_NO]: {
            ...baseTextInput,
            name: realEstateGeneralFields.BED_NO,
            tooltip: t('app.common.property.bedsNumber.tooltip'),
            label: t('app.common.property.bedsNumber'),
        },
        [realEstateGeneralFields.PARKING_SPACE_NUMBER]: {
            ...baseTextInput,
            name: realEstateGeneralFields.PARKING_SPACE_NUMBER,
            label: t('app.common.property.parkingSpaceNumber'),
        },
        [realEstateGeneralFields.BATHROOM_TOILET_NO]: {
            ...baseTextInput,
            name: realEstateGeneralFields.BATHROOM_TOILET_NO,
            tooltip: t('app.common.property.bathroomToiletNumber.tooltip'),
            label: t('app.common.property.bathroomToiletNumber'),
        },
        [realEstateGeneralFields.BATHROOM_NO]: {
            ...baseTextInput,
            name: realEstateGeneralFields.BATHROOM_NO,
            tooltip: t('app.common.property.bathroomNoToiletNumber.tooltip'),
            label: t('app.common.property.bathroomNoToiletNumber'),
        },
        [realEstateGeneralFields.COMMON_BATHROOM_NO]: {
            ...baseTextInput,
            name: realEstateGeneralFields.COMMON_BATHROOM_NO,
            label: t('app.common.property.bathroomNumber'),
            tooltip: t('app.common.property.bathroomNumber.tooltip'),
        },
        [realEstateGeneralFields.TOILET_NO]: {
            ...baseTextInput,
            name: realEstateGeneralFields.TOILET_NO,
            tooltip: t('app.common.property.toiletNumber.tooltip'),
            label: t('app.common.property.toiletNumber'),
        },
        [realEstateGeneralFields.CEILING_HEIGHT]: {
            ...baseNumeralInput,
            name: realEstateGeneralFields.CEILING_HEIGHT,
            label: t('app.common.property.ceilingHeight'),
        },
        [realEstateGeneralFields.CONSTRUCTION_YEAR]: {
            ...baseTextInput,
            name: realEstateGeneralFields.CONSTRUCTION_YEAR,
            label: t('app.common.property.constructionYear'),
        },
        [realEstateGeneralFields.ADAPTATION_YEAR]: {
            ...baseTextInput,
            name: realEstateGeneralFields.ADAPTATION_YEAR,
            label: t('app.common.property.adaptationYear'),
        },
        [realEstateGeneralFields.PROPERTY_FLOOR_ID]: {
            ...baseSelectInput,
            name: realEstateGeneralFields.PROPERTY_FLOOR_ID,
            required: true,
            label: t('app.common.numberOfFloors'),
        },
        // Etaže
        [realEstateGeneralFields.PROPERTY_FLOOR_POSITION_ID]: {
            ...baseMultiSelectInput,
            name: realEstateGeneralFields.PROPERTY_FLOOR_POSITION_ID,
            label: t('app.common.floorPosition'),
            handleInputDisable: (formik) => {
                const floorNoValue = getIn(formik?.values, `general.${realEstateGeneralFields.FLOOR_NO}`)
                return Boolean(floorNoValue)
            },
            isRequired: (formik) => {
                const floorNoValue = getIn(formik?.values, `general.${realEstateGeneralFields.FLOOR_NO}`)
                return !floorNoValue
            },
        },
        // Kat
        [realEstateGeneralFields.FLOOR_NO]: {
            ...baseTextInput,
            name: realEstateGeneralFields.FLOOR_NO,
            label: t('app.common.floor'),
            tooltip: t('form.property.general.floor.tooltip'),
            handleInputDisable: (formik) => {
                const floorPositionValue = getIn(formik?.values, `general.${realEstateGeneralFields.PROPERTY_FLOOR_POSITION_ID}`)
                return floorPositionValue && floorPositionValue.length > 0
            },
            isRequired: (formik) => {
                const floorPositionValue = getIn(formik?.values, `general.${realEstateGeneralFields.PROPERTY_FLOOR_POSITION_ID}`)
                return !(floorPositionValue && floorPositionValue.length > 0)
            },
        },
        [realEstateGeneralFields.FLOORS_TOTAL_NO]: {
            ...baseTextInput,
            name: realEstateGeneralFields.FLOORS_TOTAL_NO,
            label: t('app.common.property.floorsTotal'),
        },
        [realEstateGeneralFields.PROPERTY_ORIENTATION_ID]: {
            ...baseMultiSelectInput,
            name: realEstateGeneralFields.PROPERTY_ORIENTATION_ID,
            label: t('app.common.property.orientation'),
        },
        [realEstateGeneralFields.PROPERTY_HEATING_ID]: {
            ...baseMultiSelectInput,
            name: realEstateGeneralFields.PROPERTY_HEATING_ID,
            label: t('app.common.heating'),
        },
        [realEstateGeneralFields.PROPERTY_CONDITION_ID]: {
            ...baseMultiSelectInput,
            name: realEstateGeneralFields.PROPERTY_CONDITION_ID,
            label: t('app.common.property.condition'),
        },
        [realEstateGeneralFields.PROPERTY_WINDOWS_DOORS_ID]: {
            ...baseMultiSelectInput,
            name: realEstateGeneralFields.PROPERTY_WINDOWS_DOORS_ID,
            label: t('app.common.property.carpentry'),
        },
        [realEstateGeneralFields.PROPERTY_PUBLIC_TRANSPORTATION_ID]: {
            ...baseMultiSelectInput,
            name: realEstateGeneralFields.PROPERTY_PUBLIC_TRANSPORTATION_ID,
            label: t('app.common.property.publicTransportation'),
        },
        [realEstateGeneralFields.DISTANCE_SEA]: {
            ...baseNumeralInput,
            name: realEstateGeneralFields.DISTANCE_SEA,
            label: t('app.common.property.seaDistance'),
        },
        [realEstateGeneralFields.DISTANCE_RIVER]: {
            ...baseNumeralInput,
            name: realEstateGeneralFields.DISTANCE_RIVER,
            label: t('app.common.property.riverDistance'),
        },
        [realEstateGeneralFields.DISTANCE_BEACH]: {
            ...baseNumeralInput,
            name: realEstateGeneralFields.DISTANCE_BEACH,
            label: t('app.common.property.beachDistance'),
        },
        [realEstateGeneralFields.DISTANCE_CITY_CENTRE]: {
            ...baseNumeralInput,
            name: realEstateGeneralFields.DISTANCE_CITY_CENTRE,
            label: t('app.common.property.cityCentreDistance'),
        },
        [realEstateGeneralFields.DISTANCE_SCHOOL]: {
            ...baseNumeralInput,
            name: realEstateGeneralFields.DISTANCE_SCHOOL,
            label: t('app.common.property.schoolDistance'),
        },
        [realEstateGeneralFields.DISTANCE_PUBLIC_TRANSPORTATION]: {
            ...baseNumeralInput,
            name: realEstateGeneralFields.DISTANCE_PUBLIC_TRANSPORTATION,
            label: t('app.common.property.publicTransportationDistance'),
        },
        [realEstateGeneralFields.DISTANCE_PATHWAY]: {
            ...baseNumeralInput,
            name: realEstateGeneralFields.DISTANCE_PATHWAY,
            label: t('app.common.property.pathwayDistance'),
        },
        [realEstateGeneralFields.PROPERTY_SPECIAL_FEATURE_ID]: {
            ...baseMultiSelectInput,
            name: realEstateGeneralFields.PROPERTY_SPECIAL_FEATURE_ID,
            label: t('app.common.property.specialFeatures'),
        },
        [realEstateGeneralFields.PROPERTY_DESCRIPTION]: {
            ...baseMultiSelectInput,
            name: realEstateGeneralFields.PROPERTY_DESCRIPTION,
            label: t('app.common.property.description'),
        },
        [realEstateGeneralFields.PROPERTY_UTILITY_ID]: {
            ...baseMultiSelectInput,
            name: realEstateGeneralFields.PROPERTY_UTILITY_ID,
            label: t('app.common.utility'),
        },
        [realEstateGeneralFields.PROPERTY_EQUIPMENT_ID]: {
            ...baseMultiSelectInput,
            name: realEstateGeneralFields.PROPERTY_EQUIPMENT_ID,
            label: t('app.common.property.equipment'),
        },
        [realEstateGeneralFields.PROPERTY_SPACE_ID]: {
            ...baseCheckboxInput,
            name: realEstateGeneralFields.PROPERTY_SPACE_ID,
            label: t('app.common.property.space'),
            tooltip: t('form.property.general.propertySpace.tooltip'),
        },
        [realEstateGeneralFields.SPACE_COUNT]: {
            ...baseTextInput,
            name: realEstateGeneralFields.SPACE_COUNT,
            label: t('form.contact.profile.spaceCount'),
        },
        [realEstateGeneralFields.SPACE_AREA]: {
            ...baseTextInput,
            name: realEstateGeneralFields.SPACE_AREA,
            label: t('app.common.spaceArea'),
        },
        [realEstateGeneralFields.PROPERTY_ENERGY_EFFICIENCY_ID]: {
            ...baseSelectInput,
            name: realEstateGeneralFields.PROPERTY_ENERGY_EFFICIENCY_ID,
            label: t('app.common.energyEfficiency'),
        },
        [realEstateGeneralFields.PROPERTY_PERMIT_ID]: {
            ...baseMultiSelectInput,
            name: realEstateGeneralFields.PROPERTY_PERMIT_ID,
            label: t('app.common.property.permits'),
        },
        [realEstateGeneralFields.PROPERTY_ACCESS_ID]: {
            ...baseMultiSelectInput,
            name: realEstateGeneralFields.PROPERTY_ACCESS_ID,
            label: t('app.common.property.access'),
        },
        [realEstateGeneralFields.AGENCY_COMMISSION_ID]: {
            ...baseSelectInput,
            name: realEstateGeneralFields.AGENCY_COMMISSION_ID,
            label: t('app.common.agencyCommission'),
            tooltip: t('form.common.agencyComission.tooltip'),
        },
        [realEstateGeneralFields.PRIMARY_AGENT_ID]: {
            ...baseSelectInput,
            name: realEstateGeneralFields.PRIMARY_AGENT_ID,
            label: t('app.common.agent'),
            required: true,
            filterOptions: (options, formik) => {
                const agentIds = getIn(formik?.values, `general.${realEstateGeneralFields.AGENT_ID}`)
                
                if (!agentIds?.length) return options
                return options?.filter((o) => !agentIds.includes(o.value))
            },
        },
        [realEstateGeneralFields.AGENT_ID]: {
            ...baseMultiSelectInput,
            name: realEstateGeneralFields.AGENT_ID,
            label: t('app.common.agent'),
            filterOptions: (options, formik) => {
                const primaryAgentId = getIn(formik?.values, `general.${realEstateGeneralFields.PRIMARY_AGENT_ID}`)
                
                if (!primaryAgentId) return options
                return options?.filter((o) => o.value !== primaryAgentId?.toString())
            },
        },
    }
}

const realEstateDescriptionFieldTypes = (appSettings) => {
    const { t } = i18next

    return {
        [realEstateDescriptionFields.PROPERTY_TITLE]: {
            ...baseMultiLanguageTextInput(appSettings.languages),
            name: realEstateDescriptionFields.PROPERTY_TITLE,
            label: t('app.common.title'),
            description: t('form.property.description.title.description'),
            languageStyle: 'tabs',
            colProps: {
                sm: 12,
            },
            inputProps: {
                style: { resize: 'none', height: '108px' },
            },
            maxLenFeedback: 70,
            hint: t('form.property.description.title.hint'),
        },
        [realEstateDescriptionFields.PROPERTY_DESCRIPTION]: {
            ...baseMultiLanguageEditorInput(appSettings.languages),
            name: realEstateDescriptionFields.PROPERTY_DESCRIPTION,
            label: t('app.common.description'),
            languageStyle: 'tabs',
            colProps: {
                sm: 12,
            },
        },
    }
}

const propertyTransactionsFieldTypes = (appSettings, propertyServiceValue, requiredFields) => {
    const marketName = appSettings?.applicationMarket?.name

    const hrFields = HRtransactionFieldTypes(appSettings, propertyServiceValue, requiredFields)
    const siFields = SItransactionFieldTypes(appSettings, propertyServiceValue, requiredFields)
    const meFields = MEtransactionFieldTypes(appSettings, propertyServiceValue, requiredFields)

    const transactionFieldsConfig = {
        Hrvatska: hrFields,
        Slovenija: siFields,
        'Crna Gora': meFields,
    }

    return transactionFieldsConfig[marketName] ?? {}
}

const realEstaterealEstateMaterials3DVideoFieldTypes = () => {
    const { t } = i18next

    return {
        [realEstateMaterials3DVideoFields.VIRTUAL_WALK_URL]: {
            ...baseTextInput,
            name: realEstateMaterials3DVideoFields.VIRTUAL_WALK_URL,
            label: t('app.common.property.3DVideo.virtualWalk'),
            tooltip: t('form.property.3DVideo.virtualWalk.tooltip'),
            colProps: {
                sm: 12,
            },
        },
        [realEstateMaterials3DVideoFields.PROPERTY_VIDEO_URL]: {
            ...baseTextInput,
            name: realEstateMaterials3DVideoFields.PROPERTY_VIDEO_URL,
            label: t('app.common.video'),
            tooltip: t('form.property.3DVideo.property.tooltip'),
            colProps: {
                sm: 12,
            },
        },
    }
}

const offerBaseFieldTypes = () => {
    const { t } = i18next

    return {
        [offerModalFields.OFFER_LOCALE]: {
            ...baseSelectInput,
            name: offerModalFields.OFFER_LOCALE,
            label: t('app.common.language'),
            isClearable: false,
            colProps: {
                sm: 3,
            },
        },
        [offerModalFields.AGENT_ID]: {
            ...baseSelectInput,
            name: offerModalFields.AGENT_ID,
            label: t('app.common.agent'),
            isClearable: false,
            colProps: {
                sm: 9,
            },
        },
        [offerModalFields.CONTACT_COMPANY]: {
            type: 'contact-company-cascade',
            name: offerModalFields.CONTACT_COMPANY,
            inputs: [
                {
                    name: offerModalFields.CONTACT_ID,
                },
                {
                    name: offerModalFields.CONTACT_COMPANY_COLLECTION,
                },
            ],
        },
    }
}

const offerSendFieldTypes = () => {
    const { t } = i18next

    return {
        [offerModalFields.OFFER_CONTACT_SELECT]: {
            ...baseSelectInput,
            name: offerModalFields.OFFER_CONTACT_SELECT,
            label: t('modal.offerSend.contact'),
            isClearable: false,
            colProps: { sm: 12 },
            handleChange: (value, formik) => {
                const selectedEmailData = formik.values.emailDataGroup?.find((data) => data.contact_id === +value)
                const selectedIndex = formik.values.emailDataGroup?.findIndex((data) => data.contact_id === +value)

                if (selectedIndex !== -1 && selectedEmailData) {
                    formik.setFieldValue(`emailDataGroup.${selectedIndex}.offer_email_title`, selectedEmailData.offer_email_title)
                    formik.setFieldValue(`emailDataGroup.${selectedIndex}.offer_email_message`, selectedEmailData.offer_email_message)
                }
            },
        },
        [offerModalFields.OFFER_EMAIL_TITLE]: {
            ...baseTextInput,
            label: t('modal.offerSend.emailTitle'),
            colProps: { sm: 12 },
        },
        [offerModalFields.OFFER_EMAIL_MESSAGE]: {
            ...baseTextAreaInput,
            label: t('modal.offerSend.emailMessage'),
            colProps: { sm: 12 },
            inputProps: {
                style: { resize: 'none', height: '170px' },
            },
        },
    }
}

const propertyDetailSearchFieldTypes = () => {
    const { t } = i18next

    return {
        [propertyDetailSearchFields.INTERNAL_APP_ID]: {
            ...baseTextInput,
            name: propertyDetailSearchFields.INTERNAL_APP_ID,
            label: t('app.common.propertyId'),
            colProps: {
                sm: 12,
                xl: 4,
            },
        },
        [propertyDetailSearchFields.INTERNAL_ID]: {
            ...baseTextInput,
            name: propertyDetailSearchFields.INTERNAL_ID,
            label: t('app.common.agencyId'),
            colProps: {
                sm: 12,
                xl: 4,
            },
        },
        [propertyDetailSearchFields.PROPERTY_SERVICE]: {
            ...baseMultiSelectInput,
            name: propertyDetailSearchFields.PROPERTY_SERVICE,
            label: t('app.common.service'),
            colProps: {
                sm: 12,
                xl: 4,
            },
        },
        [propertyDetailSearchFields.PROPERTY_STATUS]: {
            ...baseMultiSelectInput,
            name: propertyDetailSearchFields.PROPERTY_STATUS,
            label: t('app.common.status'),
            colProps: {
                sm: 12,
                xl: 4,
            },
        },
        [propertyDetailSearchFields.PROPERTY_TYPE]: {
            ...baseMultiSelectInput,
            name: propertyDetailSearchFields.PROPERTY_TYPE,
            label: t('app.common.property.type'),
            colProps: {
                sm: 12,
                xl: 4,
            },
            handleChange: (value) => {
                store.dispatch(setFormFilter({ context: 'property', field: 'propertyTypeId', value }))
            },
        },
        [propertyDetailSearchFields.PROPERTY_SUB_TYPE]: {
            ...baseMultiSelectInput,
            name: propertyDetailSearchFields.PROPERTY_SUB_TYPE,
            label: t('app.common.property.subtype'),
            colProps: {
                sm: 12,
                xl: 4,
            },
        },
        [propertyDetailSearchFields.PRICE_FROM]: {
            ...basePriceInput,
            name: propertyDetailSearchFields.PRICE_FROM,
            placeholder: t('app.common.from'),
            colProps: {
                sm: 6,
                className: 'pe-0',
            },
        },
        [propertyDetailSearchFields.PRICE_TO]: {
            ...basePriceInput,
            name: propertyDetailSearchFields.PRICE_TO,
            placeholder: t('app.common.to'),
            colProps: {
                sm: 6,
            },
        },
        [propertyDetailSearchFields.AREA_FROM]: {
            ...baseTextInput,
            name: propertyDetailSearchFields.AREA_FROM,
            placeholder: t('app.common.from'),
            colProps: {
                sm: 6,
                className: 'pe-0',
            },
        },
        [propertyDetailSearchFields.AREA_TO]: {
            ...baseTextInput,
            name: propertyDetailSearchFields.AREA_TO,
            placeholder: t('app.common.to'),
            colProps: {
                sm: 6,
            },
        },
        [propertyDetailSearchFields.ROOMS_NO_FROM]: {
            ...baseTextInput,
            name: propertyDetailSearchFields.ROOMS_NO_FROM,
            placeholder: t('app.common.from'),
            colProps: {
                sm: 6,
                className: 'pe-0',
            },
        },
        [propertyDetailSearchFields.ROOMS_NO_TO]: {
            ...baseTextInput,
            name: propertyDetailSearchFields.ROOMS_NO_TO,
            placeholder: t('app.common.to'),
            colProps: {
                sm: 6,
            },
        },
        [propertyDetailSearchFields.BEDROOMS_NO_FROM]: {
            ...baseTextInput,
            name: propertyDetailSearchFields.BEDROOMS_NO_FROM,
            placeholder: t('app.common.from'),
            colProps: {
                sm: 6,
                className: 'pe-0',
            },
        },
        [propertyDetailSearchFields.BEDROOMS_NO_TO]: {
            ...baseTextInput,
            name: propertyDetailSearchFields.BEDROOMS_NO_TO,
            placeholder: t('app.common.to'),
            colProps: {
                sm: 6,
            },
        },
        [propertyDetailSearchFields.FLOOR_NO_FROM]: {
            ...baseTextInput,
            name: propertyDetailSearchFields.FLOOR_NO_FROM,
            placeholder: t('app.common.from'),
            colProps: {
                sm: 6,
                className: 'pe-0',
            },
            handleInputDisable: (formik) => {
                const propertyTypeValue = getIn(formik?.values, propertyDetailSearchFields.PROPERTY_TYPE)
                return propertyTypeValue?.some((value) => [REAL_ESTATE_TYPES.house.toString(), REAL_ESTATE_TYPES.land.toString()].includes(value))
            },
        },
        [propertyDetailSearchFields.FLOOR_NO_TO]: {
            ...baseTextInput,
            name: propertyDetailSearchFields.FLOOR_NO_TO,
            placeholder: t('app.common.to'),
            colProps: {
                sm: 6,
            },
            handleInputDisable: (formik) => {
                const propertyTypeValue = getIn(formik?.values, propertyDetailSearchFields.PROPERTY_TYPE)
                return propertyTypeValue?.some((value) => [REAL_ESTATE_TYPES.house.toString(), REAL_ESTATE_TYPES.land.toString()].includes(value))
            },
        },
        [propertyDetailSearchFields.PROPERTY_DESCRIPTIONS]: {
            ...baseMultiSelectInput,
            name: propertyDetailSearchFields.PROPERTY_DESCRIPTIONS,
            label: t('app.common.property.description'),
            colProps: {
                sm: 12,
                xl: 4,
            },
        },
        [propertyDetailSearchFields.PROPERTY_SPACE]: {
            ...baseMultiSelectInput,
            name: propertyDetailSearchFields.PROPERTY_SPACE,
            label: t('app.common.property.space'),
            colProps: {
                sm: 12,
                xl: 4,
            },
        },
        [propertyDetailSearchFields.COUNTRY]: {
            ...baseSelectInput,
            name: propertyDetailSearchFields.COUNTRY,
            label: t('app.common.country'),
            colProps: {
                sm: 12,
                xl: 4,
            },
            handleChange: (value, formik) => {
                store.dispatch(setFormFilter({ context: 'location', field: 'country', value }))

                LOCATIONS_RESET_CONFIG.country.forEach((field) => {
                    const fieldKey = field.toUpperCase()
                    formik.setFieldValue(propertyDetailSearchFields[fieldKey], [])
                })
            },
        },
        [propertyDetailSearchFields.COUNTY]: {
            ...baseMultiSelectInput,
            name: propertyDetailSearchFields.COUNTY,
            label: t('app.common.county'),
            colProps: {
                sm: 12,
                xl: 4,
            },
            handleChange: (value, formik) => {
                store.dispatch(setFormFilter({ context: 'location', field: 'county', value }))

                LOCATIONS_RESET_CONFIG.county.forEach((field) => {
                    const fieldKey = field.toUpperCase()
                    formik.setFieldValue(propertyDetailSearchFields[fieldKey], [])
                })
            },
            handleInputDisable: (formik) => {
                const countryValue = getIn(formik?.values, propertyDetailSearchFields.COUNTRY)
                return !countryValue || countryValue.length === 0
            },
        },
        [propertyDetailSearchFields.CITY]: {
            ...baseMultiSelectInput,
            name: propertyDetailSearchFields.CITY,
            label: t('app.common.cityMunicipality'),
            colProps: {
                sm: 12,
                xl: 4,
            },
            handleChange: (value, formik) => {
                store.dispatch(setFormFilter({ context: 'location', field: 'city', value }))

                LOCATIONS_RESET_CONFIG.city.forEach((field) => {
                    const fieldKey = field.toUpperCase()
                    formik.setFieldValue(propertyDetailSearchFields[fieldKey], [])
                })
            },
            handleInputDisable: (formik) => {
                const countyValue = getIn(formik?.values, propertyDetailSearchFields.COUNTY)
                return !countyValue || countyValue.length === 0
            },
        },
        [propertyDetailSearchFields.WIDER_AREA]: {
            ...baseMultiSelectInput,
            name: propertyDetailSearchFields.WIDER_AREA,
            label: t('app.common.widerArea'),
            colProps: {
                sm: 12,
                xl: 4,
            },
            handleChange: (value, formik) => {
                store.dispatch(setFormFilter({ context: 'location', field: 'widerArea', value }))

                LOCATIONS_RESET_CONFIG.wider_area.forEach((field) => {
                    const fieldKey = field.toUpperCase()
                    formik.setFieldValue(propertyDetailSearchFields[fieldKey], [])
                })
            },
            handleInputDisable: (formik) => {
                const cityValue = getIn(formik?.values, propertyDetailSearchFields.CITY)
                return !cityValue || cityValue.length === 0
            },
        },
        [propertyDetailSearchFields.QUARTER]: {
            ...baseMultiSelectInput,
            name: propertyDetailSearchFields.QUARTER,
            label: t('app.common.quarter'),
            colProps: {
                sm: 12,
                xl: 4,
            },
            handleInputDisable: (formik) => {
                const widerAreaValue = getIn(formik?.values, propertyDetailSearchFields.WIDER_AREA)
                return !widerAreaValue || widerAreaValue.length === 0
            },
        },
        [propertyDetailSearchFields.ISLAND]: {
            ...baseMultiSelectInput,
            name: propertyDetailSearchFields.ISLAND,
            label: t('app.common.island'),
            disabled: true,
            colProps: {
                sm: 12,
                xl: 4,
            },
            handleInputDisable: (formik) => {
                const countryValue = getIn(formik?.values, propertyDetailSearchFields.COUNTRY)
                return !countryValue
            },
        },
        [propertyDetailSearchFields.STREET]: {
            ...baseTextInput,
            name: propertyDetailSearchFields.STREET,
            label: t('app.common.street'),
            colProps: {
                sm: 12,
                xl: 4,
            },
        },
        [propertyDetailSearchFields.DISTANCE_SEA]: {
            ...baseTextInput,
            name: propertyDetailSearchFields.DISTANCE_SEA,
            label: t('app.common.property.seaDistance'),
            colProps: {
                sm: 12,
                xl: 4,
            },
        },
        [propertyDetailSearchFields.PROPERTY_UTILITY]: {
            ...baseMultiSelectInput,
            name: propertyDetailSearchFields.PROPERTY_UTILITY,
            label: t('app.common.utility'),
            colProps: {
                sm: 12,
                xl: 4,
            },
        },
        [propertyDetailSearchFields.PROPERTY_EQUIPMENT]: {
            ...baseMultiSelectInput,
            name: propertyDetailSearchFields.PROPERTY_EQUIPMENT,
            label: t('app.common.equipment'),
            colProps: {
                sm: 12,
                xl: 4,
            },
        },
        [propertyDetailSearchFields.OWNER]: {
            ...baseAsyncSelectInput,
            noLimit: true,
            searchCriteria: ['name'],
            name: propertyDetailSearchFields.OWNER,
            label: t('form.common.owner.nameOrCompany'),
            storeFullOption: true,
            colProps: {
                sm: 12,
                xl: 4,
            },
            useEntityHook: contacts.GetContacts.useQuery,
            formatOption: formatContactAsyncOption,
        },
        [propertyDetailSearchFields.PHONE_NUMBER]: {
            ...baseTextInput,
            name: propertyDetailSearchFields.PHONE_NUMBER,
            label: t('app.common.phoneNumber'),
            colProps: {
                sm: 12,
                xl: 4,
            },
        },
        [propertyDetailSearchFields.PRIMARY_AGENT]: {
            ...baseMultiSelectInput,
            name: propertyDetailSearchFields.PRIMARY_AGENT,
            label: t('app.common.agent'),
            colProps: {
                sm: 12,
                xl: 4,
            },
        },
        [propertyDetailSearchFields.PROPERTY_RECEIPT_DATE_FROM]: {
            ...baseDateInput,
            colProps: {
                sm: 6,
                className: 'pe-0',
            },
            name: propertyDetailSearchFields.PROPERTY_RECEIPT_DATE_FROM,
            placeholder: t('app.common.from'),
        },
        [propertyDetailSearchFields.PROPERTY_RECEIPT_DATE_TO]: {
            ...baseDateInput,
            colProps: {
                sm: 6,
            },
            name: propertyDetailSearchFields.PROPERTY_RECEIPT_DATE_TO,
            placeholder: t('app.common.to'),
        },
        [propertyDetailSearchFields.AVAILABLE_FROM_DATE_FROM]: {
            ...baseDateInput,
            colProps: {
                sm: 6,
                className: 'pe-0',
            },
            name: propertyDetailSearchFields.AVAILABLE_FROM_DATE_FROM,
            placeholder: t('app.common.from'),
        },
        [propertyDetailSearchFields.AVAILABLE_FROM_DATE_TO]: {
            ...baseDateInput,
            colProps: {
                sm: 6,
            },
            name: propertyDetailSearchFields.AVAILABLE_FROM_DATE_TO,
            placeholder: t('app.common.to'),
        },
        [propertyDetailSearchFields.WEB]: {
            ...baseSelectInput,
            name: propertyDetailSearchFields.WEB,
            label: t('form.property.detailSearch.web'),
            colProps: {
                sm: 12,
                xl: 4,
            },
            handleChange: (value, formik) => {
                const webValue = getIn(formik?.values, propertyDetailSearchFields.WEB)

                if (webValue) {
                    formik.setFieldValue(propertyDetailSearchFields.ATTRIBUTE, [])
                }
            },
        },
        [propertyDetailSearchFields.ATTRIBUTE]: {
            ...baseMultiSelectInput,
            name: propertyDetailSearchFields.ATTRIBUTE,
            label: t('form.property.detailSearch.attribute'),
            colProps: {
                sm: 12,
                xl: 4,
            },
            handleInputDisable: (formik) => {
                const webValue = getIn(formik?.values, propertyDetailSearchFields.WEB)
                return !webValue
            },
        },
        [propertyDetailSearchFields.PORTAL]: {
            ...baseMultiSelectInput,
            name: propertyDetailSearchFields.PORTAL,
            label: t('form.property.detailSearch.portals'),
            colProps: {
                sm: 12,
                xl: 4,
            },
        },
        [propertyDetailSearchFields.PROPERTY_CONDITION]: {
            ...baseMultiSelectInput,
            name: propertyDetailSearchFields.PROPERTY_CONDITION,
            label: t('form.property.detailSearch.condition'),
            colProps: {
                sm: 12,
                xl: 4,
            },
        },
        [propertyDetailSearchFields.PROPERTY_ENERGY_EFFICIENCY]: {
            ...baseMultiSelectInput,
            name: propertyDetailSearchFields.PROPERTY_ENERGY_EFFICIENCY,
            label: t('app.common.energyEfficiency'),
            colProps: {
                sm: 12,
                xl: 4,
            },
        },
        [propertyDetailSearchFields.PROPERTY_FLOOR]: {
            ...baseMultiSelectInput,
            name: propertyDetailSearchFields.PROPERTY_FLOOR,
            label: t('app.common.numberOfFloors'),
            colProps: {
                sm: 12,
                xl: 4,
            },
            handleInputDisable: (formik) => {
                const propertyTypeValue = getIn(formik?.values, propertyDetailSearchFields.PROPERTY_TYPE)
                return propertyTypeValue?.some((value) =>
                    [REAL_ESTATE_TYPES.garage.toString(), REAL_ESTATE_TYPES.land.toString(), REAL_ESTATE_TYPES.officeSpace.toString()].includes(value)
                )
            },
        },
    }
}

export {
    offerBaseFieldTypes,
    offerSendFieldTypes,
    propertyDetailSearchFieldTypes as propertyDetailSearhFieldTypes,
    propertyTransactionsFieldTypes,
    realEstateDescriptionFieldTypes,
    realEstateGeneralFieldTypes,
    realEstaterealEstateMaterials3DVideoFieldTypes,
}
